import { Button } from "@client/components/ui/button";
import { posthog } from "@client/lib/analytics";
import { captureClientException } from "@client/lib/sentry";
import { Link, useLocation } from "@tanstack/react-router";
import { FileQuestion, Home, Search } from "lucide-react";
import { useEffect } from "react";

export function NotFoundPage() {
	const location = useLocation();

	useEffect(() => {
		const path = location.pathname;

		posthog.capture("page_not_found", { path });

		captureClientException(new Error(`404 - Page not found: ${path}`), {
			tags: { type: "404", path },
		});
	}, [location.pathname]);

	return (
		<div className="flex min-h-[calc(100vh-4rem)] flex-col items-center justify-center p-6 text-center">
			{/* Visual indicator */}
			<div className="relative mb-8">
				<div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-muted">
					<FileQuestion className="h-12 w-12 text-muted-foreground" />
				</div>
				<div className="-right-2 -top-2 absolute flex h-8 w-8 items-center justify-center rounded-full bg-background ring-2 ring-border">
					<span className="font-bold text-muted-foreground text-xs">404</span>
				</div>
			</div>

			{/* Heading */}
			<h1 className="font-semibold text-2xl text-foreground tracking-tight sm:text-3xl">
				Page not found
			</h1>

			<p className="mt-3 max-w-sm text-muted-foreground text-sm leading-relaxed">
				The page you're looking for doesn't exist or has been moved. Check the
				URL or head back home.
			</p>

			{/* Actions */}
			<div className="mt-8 flex flex-col gap-3 sm:flex-row">
				<Button asChild>
					<Link to="/dashboard">
						<Home className="mr-2 h-4 w-4" />
						Go to Dashboard
					</Link>
				</Button>
				<Button variant="outline" asChild>
					<Link to="/">
						<Search className="mr-2 h-4 w-4" />
						Back to Home
					</Link>
				</Button>
			</div>

			{/* Subtle hint */}
			<p className="mt-12 text-muted-foreground/60 text-xs">
				If you believe this is a mistake, please contact support.
			</p>
		</div>
	);
}
